import {
  BaseApiFetchClientConstructor,
  FetchPlugin,
  getAlfMockAdapter,
  MockInterceptFetch,
  MockInterceptRequest,
  RequestPlugin,
  RetryFetch,
  UrlRewriteRequest,
} from '@ama-sdk/core';
import {
  Platform,
} from '@angular/cdk/platform';
import {
  EventTrackService,
} from '@o3r/analytics';
import {
  ApiManager,
} from '@o3r/apis-manager';
import {
  RequestParametersService,
} from '@o3r/dynamic-content';
import {
  LoggerService,
} from '@o3r/logger';
import {
  AppBootstrapConfig,
  BotProtectionFactory,
  ExtraPluginsByAPI,
} from '@refx/shared-common';
import {
  AppInitializationService,
  createApiManagerFactory,
} from '@refx/shared-services';
import {
  SsciQueryParams,
} from '../types/query-params.enum';
import {
  isAlfMockScenario,
  mockAdapter,
} from './mocks/index';

/**
 * Retry the call when dapi reponse is
 * Record in use - please retry
 */
export const retryRecordInUse = new RetryFetch(
  3,
  async (context, data, _error): Promise<any> => {
    if (data && data.status !== 500) {
      return false;
    }
    let body;
    try {
      body = data ? JSON.parse(await data.text()) : undefined;
    } catch {
      return {
        callResponse: body,
        value: false
      };
    }
    return {
      callResponse: body,
      value: !!body && body.errors.some((error: any) => error.code === '7001')
    };
  }
);

/**
 * Factory that creates the API manager configuration for the application
 *
 * @param bootstrapConfig
 * @param botProtectionFactory The factory for bot protection providers
 * @param eventTrackService
 * @param requestParametersService
 * @param appInitializationService
 * @param platform
 * @param extraPluginsByAPI
 * @param customApiClients
 * @param loggerService
 */
export function createApisFactorySSCI(
  bootstrapConfig: AppBootstrapConfig,
  botProtectionFactory: BotProtectionFactory,
  eventTrackService: EventTrackService,
  requestParametersService: RequestParametersService,
  appInitializationService: AppInitializationService,
  platform: Platform,
  extraPluginsByAPI: ExtraPluginsByAPI,
  customApiClients: {[key: string]: BaseApiFetchClientConstructor;} = {},
  loggerService?: LoggerService
): ApiManager {
  const requestPlugins: RequestPlugin[] = [];
  const fetchPlugins: FetchPlugin[] = [];
  const useMocks = appInitializationService.shouldUseMocks();
  if (useMocks) {
    /** Request interceptor that only kicks in if exists query parameter useMocks */
    const scenario = appInitializationService.getScenario();
    const adapter = isAlfMockScenario(scenario)
      ? getAlfMockAdapter(
        scenario!,
        () => import('@dapi/sdk/spec').then(({OPERATION_ADAPTER}) => OPERATION_ADAPTER)
      ) : mockAdapter;
    requestPlugins.push(new MockInterceptRequest({
      adapter: adapter
    }));
    fetchPlugins.push(new MockInterceptFetch({
      adapter: adapter
    }));
  } else {
    fetchPlugins.push(retryRecordInUse);
  }
  let overriddenDapiUrl: string;

  /**
   * Support for query param "dapiUrl" to support devTest URL instead
   *
   * @param url dapi URL
   */
  const rewriteUrlFun = (url: string) => {
    overriddenDapiUrl = requestParametersService.getQueryParameter(SsciQueryParams.dapiUrl) || requestParametersService.getPostParameter(SsciQueryParams.dapiUrl) || overriddenDapiUrl;
    return overriddenDapiUrl || url;
  };

  requestPlugins.push(new UrlRewriteRequest(rewriteUrlFun));

  return createApiManagerFactory(
    bootstrapConfig,
    botProtectionFactory,
    eventTrackService,
    appInitializationService,
    platform,
    fetchPlugins,
    requestPlugins,
    [],
    extraPluginsByAPI,
    undefined,
    undefined,
    customApiClients,
    undefined,
    loggerService
  );
}
